import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Reveal from "@/components/Reveal";
import GoldParticles from "@/components/GoldParticles";

const SUBJECTS = ["General Enquiry", "Events & Arenas", "Registration", "Schedule", "Participation & Delegation", "Sponsorship"];

const initial = { name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please complete all fields before sending.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received. The organising committee will respond shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden border-b border-white/5 py-24 sm:py-28">
        <GoldParticles density={30} />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow mb-5">Contact the House</p>
            <h1 data-testid="contact-heading" className="max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-[#F6F4EE]">
              Ask the<br />
              <span className="text-gold-sheen">Croupier.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-sm sm:text-base leading-relaxed text-[#A19E95]">
              Questions on events, registration, schedules or participation — the organising committee reads
              every enquiry placed on the table.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.3fr]">
          <Reveal>
            <div className="flex flex-col gap-10">
              <div>
                <p className="eyebrow mb-4">The Committee</p>
                <p className="text-sm leading-relaxed text-[#A19E95]">
                  Organising Committee, FINATEX'26<br />
                  Department of Commerce<br />
                  CHRIST (Deemed to be University), Pune Lavasa Campus<br />
                  Maharashtra, India
                </p>
              </div>
              <div>
                <p className="eyebrow mb-4">Direct Lines</p>
                <div className="flex flex-col gap-3 text-sm text-[#A19E95]">
                  <a href="mailto:finatex.lavasa@christuniversity.in" data-testid="contact-email" className="hover:text-[#D4AF37] transition-colors">
                    finatex.lavasa@christuniversity.in
                  </a>
                  <span className="font-mono text-xs text-[#6E6B63]">Response within 48 hours</span>
                </div>
              </div>
              <div className="border border-white/8 bg-[#13131A] p-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B39343]">House Rule № 1</p>
                <p className="mt-3 text-sm leading-relaxed text-[#F6F4EE]/85">
                  No question is a bad bet. Ask before you register — event rules, eligibility and group
                  registration details are all fair game.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="border border-white/8 bg-[#0F0F14] p-8 sm:p-10">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="label-lux" htmlFor="contact-name">Full Name</label>
                  <input id="contact-name" data-testid="contact-name-input" className="input-lux" value={form.name} onChange={set("name")} placeholder="Your name" />
                </div>
                <div>
                  <label className="label-lux" htmlFor="contact-email">Email Address</label>
                  <input id="contact-email" type="email" data-testid="contact-email-input" className="input-lux" value={form.email} onChange={set("email")} placeholder="you@college.edu" />
                </div>
                <div>
                  <label className="label-lux" htmlFor="contact-phone">Phone Number</label>
                  <input id="contact-phone" data-testid="contact-phone-input" className="input-lux" value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
                </div>
                <div>
                  <label className="label-lux" htmlFor="contact-subject">Enquiry Type</label>
                  <select id="contact-subject" data-testid="contact-subject-select" className="input-lux" value={form.subject} onChange={set("subject")}>
                    {SUBJECTS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="label-lux" htmlFor="contact-message">Message</label>
                  <textarea id="contact-message" rows={5} data-testid="contact-message-input" className="input-lux resize-none" value={form.message} onChange={set("message")} placeholder="Place your question on the table..." />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="group mt-8 inline-flex items-center gap-3 bg-[#D4AF37] px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-[#08080A] transition-colors duration-300 hover:bg-[#E5C97A] disabled:opacity-50"
              >
                {submitting ? "Placing Enquiry..." : "Submit Enquiry"}
                <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
